import type { FeatureFlagsDto } from '@cibli/shared';

import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';

import IdleModal from '@/features/common/components/IdleModal';
import SleepMode from '@/features/common/components/SleepMode';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useIdle } from '@/features/common/hooks/useIdle';
import { useIdleModal } from '@/features/common/hooks/useIdleModal';
import LoadingScreen from '@/features/common/pages/LoadingScreen';
import SecretPinDialog from '@/features/settings/components/SecretPinDialog';
import { useForceFullscreen } from '@/features/settings/hooks/useForceFullscreen';
import { featureFlagsService } from '@/services/featureFlags';

import { useRegisterContext } from '../contexts/register-context.ref';

const resetEventsNames = ['speaking-activity'];

export default function KioskFlowLayout() {
  useForceFullscreen({
    disabled: import.meta.env.VITE_DISABLE_FULLSCREEN === 'true',
  });

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsDto | null>(null);
  const { sleepMode, onReset } = useInterviewContext();
  const { isAutoStartingWithSession } = useRegisterContext();
  const { idle } = useIdle({ resetEventsNames });

  const devNoTimeoutEnabled = featureFlagsService.isDevNoTimeoutActivated(featureFlags);
  const isIdleModalEnabled =
    import.meta.env.VITE_DISABLE_IDLE_LOGOUT !== 'true' && !devNoTimeoutEnabled;

  const { idleModalOpen, setIdleModalOpen, handleIdleContinue, handleIdleQuit } = useIdleModal({
    enabled: isIdleModalEnabled,
    idle,
    onQuit: onReset,
  });

  useEffect(() => {
    featureFlagsService.getFeatureFlags().then(setFeatureFlags).catch(console.error);
  }, []);

  if (isAutoStartingWithSession) return <LoadingScreen />;

  return (
    <>
      <SecretPinDialog />
      {sleepMode && <SleepMode />}
      {isIdleModalEnabled && (
        <IdleModal
          open={idleModalOpen}
          onOpenChange={setIdleModalOpen}
          onContinue={handleIdleContinue}
          onQuit={handleIdleQuit}
        />
      )}
      <Outlet />
    </>
  );
}
